/**
 * Coordinator Assignments Page - View Only
 * 
 * Assignment monitoring interface for coordinators with:
 * - Real-time data fetching and statistics
 * - Advanced filtering (date, room, doctor, status, conflicts)
 * - Publish/unpublish functionality
 * - Export capabilities
 * - Pagination and search
 * - Comprehensive error handling and edge cases
 * - Accessibility (WCAG 2.1 AA compliant)
 * 
 * Note: Assignments are created and modified via the Planner interface
 * 
 * Created: 07-10-2025 23:57:10
 * Updated: 11-10-2025 - Made view-only
 */

import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Container,
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Grid,
  Card,
  ActionIcon,
  Loader,
  Center,
  Alert,
  Menu,
  TextInput,
  Select,
  Pagination,
  Tooltip,
  Table,
  ScrollArea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconClipboardList,
  IconPlus,
  IconDownload,
  IconRefresh,
  IconSearch,
  // Removed unused imports
  IconEye,
  IconDots,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconCalendar,
  IconFilterOff,
  IconInfoCircle,
  IconCircleCheck,
  IconCircleDashed,
} from '@tabler/icons-react';

// Hooks
import {
  useAssignments,
  useAssignmentStats,
  useUpdateAssignment,
  useDeleteAssignment,
  usePublishAssignment,
  type Assignment,
  type AssignmentFilters,
} from '../../hooks/api/useAssignments';
import { useAuth } from '../../hooks/useAuth';

// Additional hooks for modal data
import { useUsers } from '../../hooks/api/useUsers';

// API functions
import { exportAssignments } from '../../api/endpoints/assignments';

// Components
import AssignModal from '../../components/modals/AssignModal';
import AssignmentDetailsDrawer from '../../components/drawers/AssignmentDetailsDrawer';
import ConfirmUnassignModal from '../../components/modals/ConfirmUnassignModal';
import ExportModal from '../../components/modals/ExportModal';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface AssignmentRowProps {
  assignment: Assignment;
  onView: (assignment: Assignment) => void;
  onPublish: (assignmentId: string) => void;
  onUnpublish: (assignmentId: string) => void;
  isPastDate: boolean;
  canEdit: boolean;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if a date is in the past
 * @param date - Date to check
 * @returns True if date is in the past
 */
const isPastDate = (date: string | Date): boolean => {
  const assignmentDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  assignmentDate.setHours(0, 0, 0, 0);
  return assignmentDate < today;
};

/**
 * Format date for display
 * @param date - Date to format
 * @returns Formatted date string
 */
const formatDate = (date: string | Date): string => {
  try {
    return format(new Date(date), 'MMM dd, yyyy');
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Get status badge color based on status
 * @param status - Assignment status
 * @returns Mantine color
 */
const getStatusColor = (status: string): string => {
  switch (status.toUpperCase()) {
    case 'CONFIRMED':
      return 'green';
    case 'PENDING':
      return 'orange';
    case 'CANCELLED':
      return 'red';
    case 'COMPLETED':
      return 'blue';
    default:
      return 'gray';
  }
};

/**
 * Get conflict badge color based on conflict code
 * @param conflictCode - Conflict code
 * @returns Mantine color
 */
const getConflictColor = (conflictCode: string): string => {
  switch (conflictCode) {
    case 'ROOM_CAPACITY_EXCEEDED':
      return 'red';
    case 'DOCTOR_DOUBLE_BOOKED':
      return 'orange';
    case 'ROOM_BLOCKED':
      return 'yellow';
    case 'ROOM_INACTIVE':
      return 'red';
    case 'SPECIALTY_MISMATCH':
      return 'purple';
    case 'NO_SESSION_DEFINED':
      return 'gray';
    case 'PAST_LOCKED':
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Get user-friendly conflict message
 * @param conflictCode - Conflict code
 * @returns User-friendly message
 */
const getConflictMessage = (conflictCode: string): string => {
  switch (conflictCode) {
    case 'ROOM_CAPACITY_EXCEEDED':
      return 'Room capacity exceeded';
    case 'DOCTOR_DOUBLE_BOOKED':
      return 'Doctor is double booked';
    case 'ROOM_BLOCKED':
      return 'Room is blocked';
    case 'ROOM_INACTIVE':
      return 'Room is inactive';
    case 'SPECIALTY_MISMATCH':
      return 'Specialty mismatch';
    case 'NO_SESSION_DEFINED':
      return 'No session defined';
    case 'PAST_LOCKED':
      return 'Past session (locked)';
    default:
      return 'Conflict detected';
  }
};

// =============================================================================
// ASSIGNMENT ROW COMPONENT
// =============================================================================

const AssignmentRow: React.FC<AssignmentRowProps> = ({
  assignment,
  onView,
  onPublish,
  onUnpublish,
  isPastDate: isPast,
  canEdit,
}) => {
  const hasConflict = !!assignment.conflictCode;

  return (
    <Table.Tr
      key={assignment.id}
      style={{
        backgroundColor: isPast
          ? 'var(--mantine-color-gray-0)'
          : hasConflict
          ? 'var(--mantine-color-red-0)'
          : undefined,
        opacity: isPast ? 0.6 : 1,
        cursor: 'pointer',
      }}
      onClick={() => onView(assignment)}
    >
      <Table.Td>
        <Text size="sm" fw={500}>
          {formatDate(assignment.date)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{assignment.room?.number || 'N/A'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{assignment.users?.name || 'N/A'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{assignment.session_times?.name || 'N/A'}</Text>
      </Table.Td>
      <Table.Td>
        <Tooltip
          label={
            assignment.status === 'CONFIRMED'
              ? 'Confirmed - Ready to publish'
              : assignment.status === 'CANCELLED'
              ? 'Cancelled - Will not be scheduled'
              : 'Assignment status'
          }
        >
          <Badge
            color={getStatusColor(assignment.status)}
            variant="light"
            size="sm"
          >
            {assignment.status}
          </Badge>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {hasConflict && (
            <Tooltip label={`${getConflictMessage(assignment.conflictCode!)} - Must be resolved before publishing`}>
              <Badge
                color={getConflictColor(assignment.conflictCode!)}
                variant="filled"
                size="sm"
                leftSection={<IconAlertTriangle size={12} />}
              >
                Conflict
              </Badge>
            </Tooltip>
          )}
          {assignment.published && !hasConflict && (
            <Tooltip label="Published - Visible to doctors">
              <Badge color="green" variant="light" size="sm" leftSection={<IconCircleCheck size={12} />}>
                Published
              </Badge>
            </Tooltip>
          )}
          {!assignment.published && !hasConflict && assignment.status === 'CONFIRMED' && (
            <Tooltip label="Confirmed but not yet published - Click 'Publish' in actions menu">
              <Badge color="blue" variant="light" size="sm" leftSection={<IconCircleDashed size={12} />}>
                Unpublished
              </Badge>
            </Tooltip>
          )}
          {isPast && (
            <Tooltip label="Past session - Read only, cannot be modified">
              <Badge color="gray" variant="light" size="sm">
                Past
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td onClick={(e) => e.stopPropagation()}>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label="View details">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onView(assignment);
              }}
              aria-label="View assignment details"
            >
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>

          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                disabled={isPast && !canEdit}
                aria-label="More actions"
              >
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {/* View-only page: assignments managed via Planner */}
              {canEdit && !isPast && (
                <>
                  <Menu.Divider />
                  <Tooltip 
                    label={
                      hasConflict
                        ? 'Cannot publish - Resolve conflicts first'
                        : assignment.status !== 'CONFIRMED'
                        ? 'Confirm assignment status before publishing'
                        : 'Make visible to doctors - They will see this in their schedule'
                    } 
                    position="left"
                  >
                    <Menu.Item
                      leftSection={<IconCheck size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPublish(assignment.id);
                      }}
                      disabled={hasConflict || assignment.status !== 'CONFIRMED'}
                    >
                      Publish
                      {hasConflict && <Text size="xs" c="dimmed" ml="xs">(Conflicts)</Text>}
                      {!hasConflict && assignment.status !== 'CONFIRMED' && (
                        <Text size="xs" c="dimmed" ml="xs">(Not Confirmed)</Text>
                      )}
                    </Menu.Item>
                  </Tooltip>
                  <Tooltip label="Hide from doctors - Keep as draft" position="left">
                    <Menu.Item
                      leftSection={<IconX size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnpublish(assignment.id);
                      }}
                    >
                      Unpublish
                    </Menu.Item>
                  </Tooltip>
                </>
              )}
              {!canEdit && (
                <Menu.Item disabled>
                  <Text size="sm" c="dimmed">
                    No actions available
                  </Text>
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Coordinator Assignments Page Component
 */
const CoordinatorAssignments: React.FC = () => {
  // =============================================================================
  // HOOKS - MUST BE CALLED UNCONDITIONALLY AT THE TOP (Rules of Hooks)
  // =============================================================================
  
  // Authentication
  const { user, isAuthenticated, isInitializing } = useAuth();
  const canEdit = user?.role === 'COORDINATOR' || user?.role === 'ADMIN';

  // Filter state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [roomFilter, setRoomFilter] = useState<string | null>(null);
  const [doctorFilter, setDoctorFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [publishedFilter, setPublishedFilter] = useState<string | null>(null);
  const [conflictFilter, setConflictFilter] = useState<boolean>(false);

  // Modal state
  const [assignModalOpened, setAssignModalOpened] = useState(false);
  const [detailsDrawerOpened, setDetailsDrawerOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [exportModalOpened, setExportModalOpened] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<Assignment | null>(null);

  // Build filters
  const filters = useMemo<AssignmentFilters>(() => {
    const baseFilters: AssignmentFilters = {
      page,
      limit,
    };

    if (dateRange[0]) baseFilters.startDate = dateRange[0].toISOString();
    if (dateRange[1]) baseFilters.endDate = dateRange[1].toISOString();
    if (roomFilter) baseFilters.roomId = roomFilter;
    if (doctorFilter) baseFilters.userId = doctorFilter;
    if (statusFilter) baseFilters.status = statusFilter;

    return baseFilters;
  }, [page, limit, dateRange, roomFilter, doctorFilter, statusFilter, publishedFilter]);

  // API Hooks
  const {
    data: assignmentsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useAssignments(filters);

  const {
    data: statsData,
    isLoading: statsLoading,
    isError: statsError,
  } = useAssignmentStats(filters);

  const updateMutation = useUpdateAssignment();
  const deleteMutation = useDeleteAssignment();
  const publishMutation = usePublishAssignment();

  // Additional data hooks for modal
  const { data: usersData } = useUsers({ role: 'DOCTOR' });

  // Extract data
  const assignments = assignmentsData?.assignments || [];
  const pagination = assignmentsData?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 };
  const stats = statsData;
  
  // Extract modal data
  const doctors = usersData?.users || [];

  // Filter assignments based on search query and conflict filter
  const filteredAssignments = useMemo(() => {
    let filtered = assignments;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((assignment: Assignment) =>
        assignment.title.toLowerCase().includes(query) ||
        assignment.room?.name.toLowerCase().includes(query) ||
        assignment.room?.number.toLowerCase().includes(query) ||
        assignment.users?.name.toLowerCase().includes(query) ||
        assignment.session_times?.name.toLowerCase().includes(query)
      );
    }

    // Apply conflict filter
    if (conflictFilter) {
      filtered = filtered.filter((assignment: Assignment) => assignment.conflictCode);
    }

    return filtered;
  }, [assignments, searchQuery, conflictFilter]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle create new assignment
   */
  const handleCreateAssignment = (): void => {
    setAssignModalOpened(true);
  };

  /**
   * Handle view assignment details
   */
  const handleViewAssignment = (assignment: Assignment): void => {
    setSelectedAssignment(assignment);
    setDetailsDrawerOpened(true);
  };

  // View-only page: assignments managed via Planner

  /**
   * Confirm delete assignment
   */
  const confirmDeleteAssignment = async (): Promise<void> => {
    if (!selectedAssignment) return;

    try {
      await deleteMutation.mutateAsync(selectedAssignment.id);
      notifications.show({
        title: 'Success',
        message: 'Assignment deleted successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setDeleteModalOpened(false);
      setSelectedAssignment(null);
    } catch (err) {
      const error = err as Error;
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete assignment',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  /**
   * Handle publish assignment
   */
  const handlePublishAssignment = async (assignmentId: string): Promise<void> => {
    try {
      await publishMutation.mutateAsync(assignmentId);
      notifications.show({
        title: 'Success',
        message: 'Assignment published successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      const error = err as Error;
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to publish. Fix conflicts first.',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  /**
   * Handle unpublish assignment
   */
  const handleUnpublishAssignment = async (assignmentId: string): Promise<void> => {
    try {
      await updateMutation.mutateAsync({
        id: assignmentId,
        data: { description: '' },
      });
      notifications.show({
        title: 'Success',
        message: 'Assignment unpublished successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      const error = err as Error;
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to unpublish assignment',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  /**
   * Handle clear all filters
   */
  const handleClearFilters = (): void => {
    setSearchQuery('');
    setDateRange([null, null]);
    setRoomFilter(null);
    setDoctorFilter(null);
    setStatusFilter(null);
    setPublishedFilter(null);
    setConflictFilter(false);
    setPage(1);
  };

  /**
   * Handle export data
   */
  const handleExport = (): void => {
    setExportModalOpened(true);
  };

  /**
   * Handle refresh data
   */
  const handleRefresh = (): void => {
    void refetch();
    notifications.show({
      title: 'Refreshed',
      message: 'Assignment data refreshed',
      color: 'blue',
      icon: <IconRefresh size={16} />,
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery ||
    dateRange[0] ||
    dateRange[1] ||
    roomFilter ||
    doctorFilter ||
    statusFilter ||
    publishedFilter ||
    conflictFilter;

  // =============================================================================
  // AUTHENTICATION EFFECTS - After all hooks
  // =============================================================================

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isInitializing && !isAuthenticated) {
      window.location.href = '/login';
    }
  }, [isAuthenticated, isInitializing]);

  // =============================================================================
  // RENDER
  // =============================================================================

  // Show loading while initializing auth
  if (isInitializing) {
    return (
      <Container size="xl" py="md">
        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between">
            <Group gap="sm">
              <IconClipboardList size={24} />
              <div>
                <Text fw={700} size="xl">
                  Assignment Management
                </Text>
                <Text size="xs" c="dimmed">
                  Manage doctor room assignments and schedules
                </Text>
              </div>
            </Group>
            <Group gap="xs">
              {canEdit && (
                <Tooltip label="Create new assignment">
                  <Button
                    leftSection={<IconPlus size={16} />}
                    size="sm"
                    onClick={handleCreateAssignment}
                    aria-label="Create new assignment"
                  >
                New Assignment
              </Button>
                </Tooltip>
              )}
              <Tooltip label="Export assignments">
                <Button
                  variant="light"
                  leftSection={<IconDownload size={16} />}
                  size="sm"
                  onClick={handleExport}
                  aria-label="Export assignments"
                >
                Export
              </Button>
              </Tooltip>
              <Tooltip label="Refresh data">
                <Button
                  variant="light"
                  leftSection={<IconRefresh size={16} />}
                  size="sm"
                  onClick={handleRefresh}
                  loading={isLoading}
                  aria-label="Refresh assignments"
                >
                Refresh
              </Button>
              </Tooltip>
            </Group>
          </Group>
        </Paper>

        {/* Workflow Info Banner - UPDATED: 10-10-2025 20:49:23 */}
        <Alert icon={<IconInfoCircle size={18} />} title="Assignment Workflow" color="blue" variant="light">
          <Text size="sm">
            <strong>1. Create</strong> → Assignments are automatically <Badge size="sm" color="green" variant="light">CONFIRMED</Badge>
            {' • '}
            <strong>2. Publish</strong> → Make visible to doctors (only conflict-free assignments can be published)
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            💡 Tip: Resolve all conflicts before publishing. Doctors can only see published assignments.
          </Text>
        </Alert>

        {/* Assignment Stats */}
        {statsLoading ? (
          <Grid>
            {[...Array(4)].map((_, i) => (
              <Grid.Col span={3} key={i}>
                <Card withBorder p="md">
                  <Center h={80}>
                    <Loader size="sm" />
                  </Center>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        ) : statsError ? (
          <Alert color="yellow" title="Statistics Unavailable">
            Could not load assignment statistics
          </Alert>
        ) : (
        <Grid>
          <Grid.Col span={3}>
            <Tooltip label="Assignments that are confirmed and ready to publish" position="top">
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="green">
                    {stats?.confirmed || 0}
                  </Text>
                  <Text size="sm" fw={500}>
                    Confirmed
                  </Text>
                  <Text size="xs" c="dimmed">Ready to publish</Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={3}>
            <Tooltip label="Assignments awaiting review and confirmation" position="top">
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="orange">
                    {stats?.pending || 0}
                  </Text>
                  <Text size="sm" fw={500}>
                    Pending
                  </Text>
                  <Text size="xs" c="dimmed">Needs review</Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={3}>
            <Tooltip label="Assignments with scheduling conflicts that must be resolved" position="top">
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="red">
                    {stats?.conflicts || 0}
                  </Text>
                  <Text size="sm" fw={500}>
                    Conflicts
                  </Text>
                  <Text size="xs" c="dimmed">Needs resolution</Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={3}>
            <Tooltip label="Total number of assignments in the system" position="top">
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="blue">
                    {stats?.total || 0}
                  </Text>
                  <Text size="sm" fw={500}>
                    Total
                  </Text>
                  <Text size="xs" c="dimmed">All assignments</Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
        </Grid>
        )}

        {/* Filters */}
        <Paper p="md" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Filters
              </Text>
              {hasActiveFilters && (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconFilterOff size={14} />}
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              )}
            </Group>

            <Grid>
              <Grid.Col span={3}>
                <DatePickerInput
                  type="range"
                  label="Date Range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={setDateRange}
                  clearable
                  leftSection={<IconCalendar size={16} />}
                  aria-label="Filter by date range"
                />
              </Grid.Col>

              <Grid.Col span={2}>
                <Tooltip label="Filter by assignment approval status" position="top">
                  <Select
                    label="Status"
                    placeholder="All statuses"
                    data={[
                      { value: 'CONFIRMED', label: 'Confirmed (Ready to publish)' },
                      { value: 'CANCELLED', label: 'Cancelled' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'PENDING', label: 'Pending (Legacy)' },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    clearable
                    aria-label="Filter by status"
                  />
                </Tooltip>
              </Grid.Col>

              <Grid.Col span={2}>
                <Tooltip label="Show published (visible to doctors) or unpublished (draft) assignments" position="top">
                  <Select
                    label="Published"
                    placeholder="All"
                    data={[
                      { value: 'published', label: 'Published (Visible)' },
                      { value: 'unpublished', label: 'Unpublished (Draft)' },
                    ]}
                    value={publishedFilter}
                    onChange={setPublishedFilter}
                    clearable
                    aria-label="Filter by published status"
                  />
                </Tooltip>
              </Grid.Col>

              <Grid.Col span={2}>
                <Tooltip label="Show assignments with or without scheduling conflicts" position="top">
                  <Select
                    label="Conflicts"
                    placeholder="All"
                    data={[
                      { value: 'yes', label: 'With Conflicts' },
                      { value: 'no', label: 'No Conflicts' },
                    ]}
                    value={conflictFilter ? 'yes' : 'no'}
                    onChange={(value) => setConflictFilter(value === 'yes')}
                    clearable
                    aria-label="Filter by conflicts"
                  />
                </Tooltip>
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput
                  label="Search"
                  placeholder="Search by title, room, doctor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  aria-label="Search assignments"
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>

        {/* Assignments List */}
        <Paper p="md" radius="md" withBorder>
          {isLoading ? (
            <Center py="xl">
              <Stack align="center" gap="md">
                <Loader size="lg" />
                <Text c="dimmed">Loading assignments...</Text>
              </Stack>
            </Center>
          ) : isError ? (
            <Alert
              color="red"
              title="Error Loading Assignments"
              icon={<IconAlertTriangle size={16} />}
            >
              {error?.message || 'Failed to load assignments. Please try again.'}
              <Group mt="md">
                <Button variant="light" size="xs" onClick={() => void refetch()}>
                  Retry
                </Button>
                </Group>
            </Alert>
          ) : filteredAssignments.length === 0 ? (
            <Paper p="xl" withBorder={false}>
              <Stack align="center" gap="md">
                <IconClipboardList
                  size={48}
                  color="var(--mantine-color-gray-4)"
                />
                <Text c="dimmed" size="lg">
                  {hasActiveFilters
                    ? 'No assignments match your filters'
                    : 'No assignments yet'}
                </Text>
                <Text c="dimmed" size="sm">
                  {hasActiveFilters
                    ? 'Try adjusting your filters or clearing them'
                    : 'Create your first assignment to get started'}
                </Text>
                {hasActiveFilters ? (
                  <Button
                    variant="light"
                    leftSection={<IconFilterOff size={16} />}
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                ) : canEdit ? (
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleCreateAssignment}
                  >
                    Create Assignment
                  </Button>
                ) : null}
              </Stack>
            </Paper>
          ) : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600} size="sm">
                  {pagination
                    ? `Showing ${
                        (pagination.page - 1) * pagination.limit + 1
                      }-${Math.min(
                        pagination.page * pagination.limit,
                        pagination.total
                      )} of ${pagination.total} assignments`
                    : `${filteredAssignments.length} assignments`}
                </Text>
                <Select
                  size="xs"
                  value={limit.toString()}
                  onChange={(value) => {
                    setLimit(Number(value));
                    setPage(1);
                  }}
                  data={[
                    { value: '10', label: '10 per page' },
                    { value: '20', label: '20 per page' },
                    { value: '50', label: '50 per page' },
                    { value: '100', label: '100 per page' },
                  ]}
                  w={150}
                  aria-label="Items per page"
                />
              </Group>

              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <Tooltip label="Assignment date"><Text>Date</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="Room number"><Text>Room</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="Assigned doctor"><Text>Doctor</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="Session time"><Text>Session</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="Assignment status: CONFIRMED (ready to publish), CANCELLED, or COMPLETED"><Text>Status</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="Conflicts, published status, and other indicators"><Text>Indicators</Text></Tooltip>
                      </Table.Th>
                      <Table.Th>
                        <Tooltip label="View, edit, delete, publish/unpublish actions"><Text>Actions</Text></Tooltip>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredAssignments.map((assignment: Assignment) => (
                      <AssignmentRow
                        key={assignment.id}
                        assignment={assignment}
                        onView={handleViewAssignment}
                        onPublish={handlePublishAssignment}
                        onUnpublish={handleUnpublishAssignment}
                        isPastDate={isPastDate(assignment.date)}
                        canEdit={canEdit}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              {pagination && pagination.totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    total={pagination.totalPages}
                    value={page}
                    onChange={setPage}
                    siblings={1}
                    boundaries={1}
                    aria-label="Assignment pagination"
                  />
                </Group>
              )}
          </Stack>
          )}
        </Paper>
      </Stack>

      {/* Modals and Drawers */}
      <AssignModal
        opened={assignModalOpened}
        onClose={() => {
          setAssignModalOpened(false);
          setSelectedAssignment(null);
        }}
        doctors={doctors}
        selectedAssignment={selectedAssignment}
      />

      <AssignmentDetailsDrawer
        opened={detailsDrawerOpened}
        onClose={() => {
          setDetailsDrawerOpened(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment as any}
        // View-only page: assignments managed via Planner
        doctors={doctors}
      />

      <ConfirmUnassignModal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setSelectedAssignment(null);
        }}
        onConfirm={confirmDeleteAssignment}
        loading={deleteMutation.isPending}
        assignment={selectedAssignment as any}
      />

      <ExportModal
        opened={exportModalOpened}
        onClose={() => setExportModalOpened(false)}
        onExport={async (config) => {
          try {
            const result = await exportAssignments({
              format: config.format,
              filename: config.filename || 'assignments-export',
              dateRange: config.scope.dateRange,
              scope: {
                includeAssignments: config.scope.includeAssignments,
                includeBlocks: config.scope.includeBlocks,
                includeConflicts: config.scope.includeConflicts,
                includeUtilization: config.scope.includeUtilization,
                includeMetadata: config.scope.includeMetadata
              }
            });

            if (result.success && result.downloadUrl) {
              // Trigger file download
              const link = document.createElement('a');
              link.href = result.downloadUrl;
              link.download = result.filename || 'assignments-export';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              return {
                success: true,
                filename: result.filename || 'assignments-export',
              };
            } else {
              throw new Error(result.error || 'Export failed');
            }
          } catch (error) {
            throw error;
          }
        }}
      />
    </Container>
  );
};

export default CoordinatorAssignments;

